import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Banknote, Check, ChevronRight, Copy, CreditCard, ExternalLink, FileText, Landmark, Mail, MessageSquare, Plus, Search, Share2, Wrench, X } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { apiUrl } from '@/lib/apiUrl'
import { usePermission } from '@/hooks/usePermission'
import { useOnline } from '@/hooks/useOnline'
import { ErrorState } from '@/components/ui/QueryState'
import { useNetworkAction } from '@/components/ui/OfflineActions'
import { useTenantBrandKit } from '@/lib/tenantBrandKit'
import { SuppressCreateMenu } from '@/components/Navigation/CreateMenu'
import { toneForStatus } from '@/lib/phaseTone'
import { cn } from '@/lib/utils'
import { invalidateWorkEntities } from '@/hooks/useApi'
import { requestReviewAfterPaid } from '@/lib/billing'
import { formatDateOnly } from '@/lib/dateOnly'
import { getInitials } from '@/utils/formatting'
import { daysOverdue } from '@/lib/invoiceState'
import { cardFeePreviewCents, stripeCostEstimateCents, type CardFeeSettings } from '@/lib/cardFees'
import { CardPayment, apiError, dollars, phoneOf, type CardDone, type CardMode, type Channel, type Contact } from './CardPayment'
import './payments.css'

/**
 * Take payment — the + menu's payment flow.
 *
 * One question per screen, modelled on the collect-payment flows in Jobber,
 * Cash App and Monzo: who is paying → which bill → how much and how → the
 * one thing that method needs → done. Card and bank payments happen only on
 * Stripe's hosted page (CardPayment). Back-office work
 * (refunds, check clearance, financing status, overrides) is Billing tools.
 */

type Invoice = { id: string; invoiceNumber: string; contactId: string; dealId?: string | null; status: string; total: number; amountPaid: number; dueDate?: string | null }
type Options = { contacts: Contact[]; invoices: Invoice[]; timezone?: string | null; cardFees?: CardFeeSettings | null }
type Method = 'card' | 'cash' | 'check' | 'financing'
type Step = 'customer' | 'bill' | 'amount' | 'finish' | 'done'
type Done =
  | CardDone
  | { kind: 'recorded' | 'pending'; amountCents: number; paymentId?: string }
  | { kind: 'financing'; amountCents: number; url: string; sentVia: Channel[]; sendErrors: string[] }

// Statuses the billing API accepts for collection (see recordOffline).
const CLOSED = ['DRAFT', 'VOIDED', 'CANCELLED', 'WRITTEN_OFF', 'PAID']
const STEPS: Step[] = ['customer', 'bill', 'amount', 'finish']
const EVERYONE_LIMIT = 30

const rawOf = (cents: number) => (cents / 100).toFixed(cents % 100 ? 2 : 0)
const balanceCents = (i: Invoice) => Math.max(0, Math.round((i.total - i.amountPaid) * 100))
const nameOf = (c?: Contact) => (c ? `${c.firstName} ${c.lastName}`.trim() : '')
// "[DEMO] Pat Homeowner" → "PH", never "[P".
export const initials = (c?: Contact) => (c ? getInitials(nameOf(c)) : '')
const today = () => new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
// "3600.5" → "3,600.5" while typing; the raw string stays unformatted.
const grouped = (raw: string) => {
  const [i, d] = raw.split('.')
  const int = (i ?? '').replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return d === undefined ? int : `${int || '0'}.${d}`
}

export function PaymentSheet() {
  const [query] = useSearchParams()
  const navigate = useNavigate()
  // Route-gated too (RequirePermission); Crew never record payments (UX-010).
  const staff = usePermission('recordPayment')
  const online = useOnline()
  const net = useNetworkAction()
  const business = useTenantBrandKit().businessName

  const [options, setOptions] = useState<Options | null>(null)
  const [step, setStep] = useState<Step>('customer')
  const [customerId, setCustomerId] = useState('')
  const [invoiceId, setInvoiceId] = useState('') // '' past the bill step = quick payment
  const [method, setMethod] = useState<Method | null>(null)
  const [cardMode, setCardMode] = useState<CardMode>(null)
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [channel, setChannel] = useState<'in_person' | 'sms' | 'email' | 'phone'>('in_person')
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // The options load failed: shown as the shared error card with Try again,
  // never as a live, empty form (UX-014 / UX-018).
  const [loadError, setLoadError] = useState<unknown>(null)
  const [done, setDone] = useState<Done | null>(null)
  const [copied, setCopied] = useState(false)
  const amountRef = useRef<HTMLInputElement>(null)

  const deepInvoice = query.get('invoiceId')
  const load = () => {
    setLoadError(null)
    return apiClient.get('/api/payments/options')
      .then(res => {
        const data: Options = res.data.data
        setOptions(data)
        const inv = data.invoices.find(i => i.id === deepInvoice)
        if (inv) { setCustomerId(inv.contactId); pickInvoice(inv); setStep('amount') }
      })
      .catch((e: unknown) => setLoadError(e))
  }
  useEffect(() => {
    if (!staff) return
    void load()
  }, [staff]) // eslint-disable-line react-hooks/exhaustive-deps
  // Back online after a failed load: try again by itself.
  useEffect(() => {
    if (online && loadError && !options) void load()
  }, [online]) // eslint-disable-line react-hooks/exhaustive-deps

  const tz = options?.timezone ?? null
  const lateDays = (i: Invoice) => daysOverdue(i.dueDate, tz)
  // Oldest debt first: the bill most overdue leads, then the biggest (UX-045).
  const open = useMemo(
    () => (options?.invoices ?? [])
      .filter(i => !CLOSED.includes(i.status) && balanceCents(i) > 0)
      .sort((a, b) => daysOverdue(b.dueDate, tz) - daysOverdue(a.dueDate, tz) || balanceCents(b) - balanceCents(a)),
    [options, tz],
  )
  const dueByContact = useMemo(() => {
    const m = new Map<string, number>()
    open.forEach(i => m.set(i.contactId, (m.get(i.contactId) ?? 0) + balanceCents(i)))
    return m
  }, [open])
  // Days the contact's oldest open bill is past due (0 = nothing late).
  const lateByContact = useMemo(() => {
    const m = new Map<string, number>()
    open.forEach(i => m.set(i.contactId, Math.max(m.get(i.contactId) ?? 0, daysOverdue(i.dueDate, tz))))
    return m
  }, [open, tz])
  const [owes, everyone] = useMemo(() => {
    const q = search.trim().toLowerCase()
    const digits = q.replace(/\D/g, '')
    const hits = (options?.contacts ?? []).filter(c => !q || nameOf(c).toLowerCase().includes(q) || (!!digits && (c.phone ?? '').replace(/\D/g, '').includes(digits)))
    return [
      hits.filter(c => dueByContact.has(c.id)).sort((a, b) => (lateByContact.get(b.id) ?? 0) - (lateByContact.get(a.id) ?? 0) || dueByContact.get(b.id)! - dueByContact.get(a.id)!),
      hits.filter(c => !dueByContact.has(c.id)).sort((a, b) => nameOf(a).localeCompare(nameOf(b))),
    ]
  }, [options, search, dueByContact, lateByContact])

  const customer = options?.contacts.find(c => c.id === customerId)
  const invoice = open.find(i => i.id === invoiceId)
  const bills = open.filter(i => i.contactId === customerId)
  const due = invoice ? balanceCents(invoice) : 0
  const amountCents = amount && amount !== '.' && /^\d*\.?\d{0,2}$/.test(amount) ? Math.round(Number(amount) * 100) : 0
  const overpay = !!invoice && amountCents > due
  const amountOk = amountCents > 0 && !overpay
  // UX-146 / DECISIONS §22: the fee is in front of staff before they choose.
  const feeOn = !!options?.cardFees?.passToCustomer
  const feePreview = cardFeePreviewCents(amountCents, options?.cardFees)
  const stripeCost = stripeCostEstimateCents(amountCents)
  const cardRowNote = feeOn
    ? feePreview > 0
      ? `Customer pays ${dollars(amountCents)} + ${dollars(feePreview)} card fee · Bank transfer isn’t offered while you pass card fees on`
      : `Scan to pay, or send a link for ${dollars(amountCents)}`
    : `You pay Stripe ≈ ${dollars(stripeCost.card)} by card, ≈ ${dollars(stripeCost.bank)} by bank`
  const stepIndex = step === 'done' ? STEPS.length : STEPS.indexOf(step)
  const firstStep = deepInvoice ? 'amount' : 'customer'

  function pickInvoice(inv: Invoice | null) {
    setInvoiceId(inv?.id ?? '')
    setAmount(inv ? rawOf(balanceCents(inv)) : '')
    setMethod(null)
  }
  const go = (s: Step) => { setError(''); setStep(s) }
  const back = () => {
    if (step === 'finish' && cardMode) return setCardMode(null)
    go(step === 'bill' ? 'customer' : step === 'amount' ? 'bill' : 'amount')
  }
  // Leave the flow: back where they came from, or Home on a fresh tab.
  const exit = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) navigate(-1)
    else navigate('/dashboard')
  }
  const chooseMethod = (m: Method) => {
    setMethod(m); setCardMode(null)
    if (m === 'cash') setNote(`Cash received ${today()}`)
    if (m === 'check') setNote('')
    go('finish')
  }
  const onAmount = (value: string) => {
    const v = value.replace(/[^\d.]/g, '')
    const [i, ...rest] = v.split('.')
    setAmount(rest.length ? `${i}.${rest.join('').slice(0, 2)}` : i)
  }
  const updateContact = (c: Contact) => setOptions(o => o && { ...o, contacts: o.contacts.map(x => (x.id === c.id ? c : x)) })

  const queryClient = useQueryClient()
  const post = async (path: string, body: unknown) => (await apiClient.post(`/api/payments/${path}`, body)).data.data
  const run = async (work: () => Promise<Done>) => {
    setBusy(true); setError('')
    try {
      setDone(await work()); setStep('done')
      // The invoice drawer, Work board and Home read these; refresh them so
      // "Back to invoice" shows the payment (UX-067).
      invalidateWorkEntities(queryClient)
    } catch (e) { setError(apiError(e, 'That didn’t go through. Check the details and try again.')) } finally { setBusy(false) }
  }
  const record = () => run(async () => {
    const m = method as 'cash' | 'check'
    const res = invoice
      ? await post('offline', { invoiceId: invoice.id, amountCents, method: m, offlineRef: note.trim() })
      : await post('quick', { customerId, amountCents, method: m, offlineRef: note.trim() })
    // Cash that clears the bill is the "paid" moment: ask for a review, as
    // card payments do through the webhook. Never blocks the receipt.
    if (m === 'cash' && invoice && amountCents >= due) requestReviewAfterPaid(invoice.contactId, invoice.id)
    return { kind: m === 'check' ? 'pending' : 'recorded', amountCents, paymentId: res.payment?.id ?? res.id }
  })
  const offerFinancing = () => run(async () => {
    const res = await post('financing', { dealId: invoice!.dealId, invoiceId: invoice!.id, offeredVia: channel })
    return { kind: 'financing', amountCents: due, url: res.linkUrlSent, sentVia: [], sendErrors: [] }
  })
  // Send (or resend) a link that already exists, from the done screen.
  const resend = async (via: Channel) => {
    if (!done || !('url' in done)) return
    const what = done.kind === 'financing' ? 'financing application link' : 'payment link'
    setBusy(true)
    try {
      await apiClient.post('/api/inbox/send', {
        contactId: customerId,
        channel: via,
        ...(via === 'email' ? { subject: `Your ${what} from ${business || 'us'}` } : {}),
        body: `Hi ${customer?.firstName ?? 'there'}, here’s your secure ${what} from ${business || 'us'}${invoice ? ` for invoice ${invoice.invoiceNumber}` : ''}: ${done.url}`,
      }, { headers: { 'Idempotency-Key': crypto.randomUUID() } })
      setDone({ ...done, sentVia: [...new Set([...done.sentVia, via])], sendErrors: [] })
    } catch (e) { setDone({ ...done, sendErrors: [apiError(e, 'The message didn’t send.')] }) } finally { setBusy(false) }
  }
  const copy = (text: string) => navigator.clipboard.writeText(text)
    .then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1600) })
    .catch(() => setError('Copy isn’t available here. Press and hold the link to copy it.'))
  const reset = () => {
    setDone(null); setMethod(null); setCardMode(null); setInvoiceId(''); setCustomerId(''); setAmount(''); setNote(''); setSearch('')
    setStep('customer')
    if (deepInvoice) navigate('/payments', { replace: true })
  }

  if (query.get('quoteId')) return <Navigate to={`/payments/tools?${query.toString()}`} replace />
  if (!staff) return null
  // Nothing can be recorded, charged or sent without a connection.
  const offlineWhy = online ? null : 'You’re offline. Connect to take a payment.'

  const who = (
    <p className="pay-who">
      <span className="pay-av" aria-hidden>{initials(customer)}</span>{nameOf(customer)}
      {invoice && <span className="pay-who-sub">· {invoice.invoiceNumber}</span>}
    </p>
  )
  const customerRow = (c: Contact, sub: ReactNode) => (
    <li key={c.id}>
      <button type="button" className="pay-row" onClick={() => { setCustomerId(c.id); pickInvoice(null); go('bill') }}>
        <span className="pay-av" aria-hidden>{initials(c)}</span>
        <span className="pay-row-l"><b>{nameOf(c)}</b>{sub && <span>{sub}</span>}</span>
        <ChevronRight className="pay-chev" aria-hidden />
      </button>
    </li>
  )
  const sentTo = (via: Channel[]) => via.map(v => (v === 'sms' ? `text to ${phoneOf(customer?.phone)}` : `email to ${customer?.email}`)).join(' and ')
  const linkCard = (url: string) => (
    <div className="ds-set-card pay-link">
      <a href={url} target="_blank" rel="noopener noreferrer" className="pay-link-url">{url.replace(/^https?:\/\//, '')}<ExternalLink aria-hidden /></a>
      <div className="pay-link-acts">
        <button type="button" className="ds-btn ds-btn-lg" onClick={() => void copy(url)}>{copied ? <Check aria-hidden /> : <Copy aria-hidden />}{copied ? 'Copied' : 'Copy'}</button>
        {customer?.phone && <button type="button" className="ds-btn ds-btn-lg" disabled={busy} onClick={() => void resend('sms')}><MessageSquare aria-hidden /> Text</button>}
        {customer?.email && <button type="button" className="ds-btn ds-btn-lg" disabled={busy} onClick={() => void resend('email')}><Mail aria-hidden /> Email</button>}
      </div>
    </div>
  )

  return (
    <div className="page-container pay-page">
      <SuppressCreateMenu />
      <section className="pay-panel" aria-label="Take payment">
        <div className="pay-top">
          {(step === firstStep && !cardMode) || step === 'done'
            ? <button type="button" className="pay-iconbtn" onClick={exit} aria-label="Close"><X aria-hidden /></button>
            : <button type="button" className="pay-iconbtn" onClick={back} aria-label="Back"><ArrowLeft aria-hidden /></button>}
          <div className="pay-top-c">
            <span className="pay-top-t">Take payment</span>
            <ol className="pay-dots" aria-label={step === 'done' ? 'Finished' : `Step ${stepIndex + 1} of ${STEPS.length}`}>
              {STEPS.map((s, i) => <li key={s} className={cn(i < stepIndex && 'is-done', i === stepIndex && 'is-on')} aria-current={i === stepIndex ? 'step' : undefined} />)}
            </ol>
          </div>
          <span className="pay-iconbtn is-ghost" aria-hidden />
        </div>

        {error && <p role="alert" className="pay-error">{error}</p>}
        {offlineWhy && options && step !== 'done' && <p role="status" className="pay-note is-ask" data-testid="pay-offline">{offlineWhy}</p>}

        {!options && loadError != null && (
          <div className="pay-step" key="load-error">
            <ErrorState error={loadError} action="load customers and invoices" onRetry={load} />
          </div>
        )}

        {/* 1 — Who is paying? */}
        {step === 'customer' && (options || loadError == null) && (
          <div className="pay-step" key="customer">
            <h2 className="pay-q">Who’s paying?</h2>
            <label className="ds-field pay-search">
              <Search aria-hidden />
              <input className="ds-inp" placeholder="Search name or phone" aria-label="Search customers" value={search} onChange={e => setSearch(e.target.value)} />
            </label>
            {!options && loadError == null && (
              <ul className="ds-set-card pay-list" aria-label="Loading customers">
                {[0, 1, 2, 3].map(i => <li key={i} className="pay-row pay-skel"><span className="pay-av shimmer" /><span className="pay-row-l"><i className="shimmer" /><i className="shimmer" /></span></li>)}
              </ul>
            )}
            {owes.length > 0 && (
              <>
                <h3 className="pay-sec">Owes you</h3>
                <ul className="ds-set-card pay-list">{owes.map(c => {
                  const late = lateByContact.get(c.id) ?? 0
                  return customerRow(c, <>{dollars(dueByContact.get(c.id)!)} due{late > 0 && <em className="pay-late"> · {late} {late === 1 ? 'day' : 'days'} overdue</em>}</>)
                })}</ul>
              </>
            )}
            {options && (
              <>
                <h3 className="pay-sec">{owes.length ? 'Everyone else' : 'Customers'}</h3>
                <ul className="ds-set-card pay-list">
                  {everyone.slice(0, EVERYONE_LIMIT).map(c => customerRow(c, phoneOf(c.phone) || c.email))}
                  <li>
                    {/* Adding a customer needs the server: offline it waits, and says so. */}
                    <button type="button" className="pay-row" {...net()} onClick={() => navigate('/customers/people?new=1')}>
                      <span className="pay-av is-add" aria-hidden><Plus /></span>
                      <span className="pay-row-l"><b>New customer</b><span>Add them, then come back to take payment</span></span>
                    </button>
                  </li>
                </ul>
                {everyone.length > EVERYONE_LIMIT && <p className="pay-note">Showing {EVERYONE_LIMIT} of {everyone.length}. Search to find someone else.</p>}
                {search && !owes.length && !everyone.length && <p className="pay-note">No customers match “{search}”.</p>}
              </>
            )}
            <button type="button" className="pay-foot-link" onClick={() => navigate('/payments/tools')}>
              <Wrench aria-hidden /> Refunds, check clearing and other billing tools
            </button>
          </div>
        )}

        {/* 2 — Which bill? */}
        {step === 'bill' && (
          <div className="pay-step" key="bill">
            {who}
            <h2 className="pay-q">What are they paying for?</h2>
            <div className="pay-bills">
              {bills.map(i => (
                <button
                  key={i.id}
                  type="button"
                  className={cn('ds-pastel-card pay-bill', `tn-${toneForStatus(lateDays(i) > 0 ? 'OVERDUE' : i.status, 'invoice')}`)}
                  onClick={() => { pickInvoice(i); go('amount') }}
                >
                  <span className="pay-bill-t">{i.invoiceNumber}{lateDays(i) > 0 && <span className="ds-pill-dark">{lateDays(i)} {lateDays(i) === 1 ? 'day' : 'days'} overdue</span>}</span>
                  <span className="pay-bill-v">{dollars(balanceCents(i))}</span>
                  <span className="pay-bill-s">
                    {i.amountPaid > 0 ? `Balance due · ${dollars(Math.round(i.total * 100))} total` : 'Balance due'}
                    {lateDays(i) === 0 && i.dueDate ? ` · due ${formatDateOnly(i.dueDate, 'short', tz)}` : ''}
                  </span>
                </button>
              ))}
              <button type="button" className="ds-pastel-card tn-neutral pay-bill pay-bill-quick" onClick={() => { pickInvoice(null); go('amount') }}>
                <span className="pay-bill-t">{bills.length ? 'Something else' : 'No open invoices'}</span>
                <span className="pay-bill-s">Record cash or a check without an invoice. The office matches it to the job later. Cards need an invoice.</span>
              </button>
            </div>
          </div>
        )}

        {/* 3 — How much, and how? */}
        {step === 'amount' && (
          <div className="pay-step" key="amount">
            {who}
            <label className="pay-amount" onClick={() => amountRef.current?.focus()}>
              <span className="pay-amount-cur" aria-hidden>$</span>
              <input
                ref={amountRef}
                className="pay-amount-inp"
                inputMode="decimal"
                placeholder="0"
                value={grouped(amount)}
                onChange={e => onAmount(e.target.value)}
                style={{ width: `${Math.max(1, grouped(amount).length - (grouped(amount).split(',').length - 1) * 0.6)}ch` }}
                aria-label="Amount ($)"
              />
            </label>
            <p className={cn('pay-amount-sub', overpay && 'is-bad')}>
              {!invoice ? 'No invoice · cash or check'
                : overpay ? `That’s more than the ${dollars(due)} due`
                : amountCents === due ? 'Full balance'
                : amountCents ? `Part payment · ${dollars(due - amountCents)} left after this`
                : `${dollars(due)} due`}
            </p>
            {invoice && amountCents !== due && (
              <button type="button" className="pay-chip" onClick={() => setAmount(rawOf(due))}>Use full balance</button>
            )}

            <h3 className="pay-sec">How are they paying?</h3>
            <ul className="ds-set-card pay-list">
              {invoice && (
                <li>
                  <button type="button" className="pay-row" disabled={!amountOk || !online} onClick={() => chooseMethod('card')}>
                    <span className="ds-ico pay-ico is-white" aria-hidden><CreditCard /></span>
                    <span className="pay-row-l"><b>{feeOn ? 'Card' : 'Card or bank'}</b><span>{!amountOk ? 'Enter an amount first' : cardRowNote}</span></span>
                    <ChevronRight className="pay-chev" aria-hidden />
                  </button>
                </li>
              )}
              <li>
                <button type="button" className="pay-row" disabled={!amountOk || !online} onClick={() => chooseMethod('cash')}>
                  <span className="ds-ico pay-ico" aria-hidden><Banknote /></span>
                  <span className="pay-row-l"><b>Cash</b><span>Money in hand, counts as paid now</span></span>
                  <ChevronRight className="pay-chev" aria-hidden />
                </button>
              </li>
              <li>
                <button type="button" className="pay-row" disabled={!amountOk || !online} onClick={() => chooseMethod('check')}>
                  <span className="ds-ico pay-ico" aria-hidden><FileText /></span>
                  <span className="pay-row-l"><b>Check</b><span>Pending until it clears</span></span>
                  <ChevronRight className="pay-chev" aria-hidden />
                </button>
              </li>
            </ul>

            {invoice?.dealId && (
              <button type="button" className="pay-alt" disabled={!online} onClick={() => chooseMethod('financing')}>
                <span className="ds-ico" aria-hidden><Landmark /></span>
                <span className="pay-row-l"><b>Can’t pay today?</b><span>Offer monthly payments through Acorn financing</span></span>
                <ChevronRight className="pay-chev" aria-hidden />
              </button>
            )}
          </div>
        )}

        {/* 4 — The one thing each method needs */}
        {step === 'finish' && method && (
          <div className="pay-step" key="finish">
            {who}
            <p className="pay-big">{dollars(method === 'financing' ? due : amountCents)}</p>
            <p className="pay-big-sub">
              {method === 'cash' ? 'Cash' : method === 'check' ? 'Check' : method === 'card' ? (feeOn ? 'Card, through Stripe' : 'Card or bank, through Stripe') : 'Financing application'}
            </p>

            {(method === 'cash' || method === 'check') && (
              <>
                <h3 className="pay-sec">{method === 'cash' ? 'Note for the office' : 'Check number'}</h3>
                <input
                  className="ds-input"
                  aria-label={method === 'cash' ? 'Cash note' : 'Check number'}
                  placeholder={method === 'cash' ? 'Receipt number or note' : 'e.g. 1042'}
                  inputMode={method === 'check' ? 'numeric' : 'text'}
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  autoFocus
                />
                <p className={cn('pay-note', !note.trim() && 'is-ask')}>
                  {!note.trim()
                    ? method === 'cash' ? 'Add a note so the office can match this cash to the job.' : 'Enter the check number to record it.'
                    : method === 'cash' ? 'Counts as paid as soon as you record it.' : 'Shows as pending until the office marks it cleared.'}
                </p>
                <button type="button" className="btn-primary pay-cta" disabled={busy || !amountOk || !note.trim() || !online} onClick={() => void record()}>
                  {busy ? 'Recording…' : `Record ${dollars(amountCents)} ${method}`}
                </button>
              </>
            )}

            {method === 'card' && invoice && customer && (
              // Offline: every button in the card flow is disabled natively.
              <fieldset disabled={!online} className="contents">
              <CardPayment
                invoice={invoice}
                customer={customer}
                amountCents={amountCents}
                cardFees={options?.cardFees ?? null}
                business={business}
                mode={cardMode}
                setMode={setCardMode}
                onContact={updateContact}
                onDone={d => { setDone(d); setCardMode(null); go('done') }}
              />
              </fieldset>
            )}

            {method === 'financing' && (
              <>
                <h3 className="pay-sec">Where are you offering it?</h3>
                <div className="ds-segment pay-seg" role="radiogroup" aria-label="Offer channel">
                  {([['in_person', 'In person'], ['sms', 'Text'], ['email', 'Email'], ['phone', 'Phone']] as const).map(([v, l]) => (
                    <button key={v} type="button" role="radio" aria-checked={channel === v} className={cn('ds-segment-item pay-seg-item', channel === v && 'ds-segment-item-active')} onClick={() => setChannel(v)}>{l}</button>
                  ))}
                </div>
                <p className="pay-note">The customer applies with Acorn on their own phone. Financing isn’t money in the bank yet: the invoice stays open until the funds arrive.</p>
                <button type="button" className="btn-primary pay-cta" disabled={busy || !online} onClick={() => void offerFinancing()}>
                  {busy ? 'Creating link…' : 'Create financing link'}
                </button>
              </>
            )}
          </div>
        )}

        {/* 5 — Done */}
        {step === 'done' && done && (
          <div className="pay-step pay-done" key="done">
            <span className={cn('pay-done-ic', done.kind === 'recorded' || done.kind === 'card' ? 'tn-mint' : 'tn-neutral')} aria-hidden>
              {(done.kind === 'link' || done.kind === 'financing') && !done.sentVia.length ? <Share2 /> : <Check />}
            </span>
            <p className="pay-big">{dollars(done.amountCents)}</p>
            <h2 className="pay-q">
              {done.kind === 'recorded' ? 'Payment recorded'
                : done.kind === 'card' ? 'Payment approved'
                : done.kind === 'pending' ? 'Check recorded'
                : done.kind === 'bank-pending' ? 'Bank payment started'
                : 'sentVia' in done && done.sentVia.length ? `Link sent to ${customer?.firstName}`
                : done.kind === 'link' ? 'Payment link ready' : 'Financing link ready'}
            </h2>
            <p className="pay-note">
              {done.kind === 'recorded' && `${nameOf(customer)}’s payment is in the books.`}
              {done.kind === 'card' && 'Paid through Stripe. The invoice is updated, and Stripe emails their receipt.'}
              {done.kind === 'pending' && 'It’s pending until the office marks the check cleared.'}
              {done.kind === 'bank-pending' && 'Bank payments take a few business days to clear. The invoice updates itself when it does.'}
              {done.kind === 'link' && (done.sentVia.length ? `Sent by ${sentTo(done.sentVia)}. The invoice updates itself when they pay.` : 'Send it to the customer. The invoice updates itself when they pay.')}
              {done.kind === 'financing' && (done.sentVia.length ? `Sent by ${sentTo(done.sentVia)}. Update their application status in Billing tools.` : `Send it to ${customer?.firstName}. Update their status in Billing tools.`)}
            </p>
            {(done.kind === 'link' || done.kind === 'financing') && done.sendErrors.length > 0 && (
              <p role="alert" className="pay-error">{done.sendErrors.join(' ')} {done.sentVia.length ? '' : 'Copy the link and send it yourself.'}</p>
            )}
            {(done.kind === 'link' || done.kind === 'financing') && linkCard(done.url)}
            {done.kind === 'recorded' && done.paymentId && (
              <a className="ds-btn ds-btn-lg" href={apiUrl(`/api/payments/${done.paymentId}/receipt`)} target="_blank" rel="noopener noreferrer">View receipt</a>
            )}
            <div className="pay-done-acts">
              <button type="button" className="btn-primary pay-cta" onClick={exit}>{deepInvoice ? 'Back to invoice' : 'Done'}</button>
              <button type="button" className="ds-btn ds-btn-lg ds-btn-ghost" onClick={reset}>Take another payment</button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
